import { isNil } from 'lodash';
import permissions from 'api/Apollo/permissions';
import useTrackWithUser from 'api/hooks/useTrackWithUser';
import useUpsertPipelineSchemaLibrary from 'api/pipeline-schema-libraries/upsert/useUpsertPipelineSchemaLibrary';
import { useControlProxy } from 'api/control/useControlProxy';
import { METHOD, PATH } from 'api/control/control-api';

import { useUserContext } from '@ion/user';

import extractPipelineSchemaLibraryData from './extractPipelineSchemaLibraryData';
import prepareSchemasForControlApi from './prepareSchemasForControlApi';

const ADDED_MSG = 'Schema Library added to Pipeline.';
const UPDATED_MSG = 'Schema Library updated on Pipeline.';

function useDeployPipelineSchemas({ pipelineSchemaLibraryId, pipeline, ...options }) {
  const [trackWithUser] = useTrackWithUser('schema_library_deployed_to_pipeline');
  const { user } = useUserContext();

  const successMsg = isNil(pipelineSchemaLibraryId) ? ADDED_MSG : UPDATED_MSG;

  const [deployToControlApi] = useControlProxy({ permission: permissions.createSchemas });
  const [upsertPipelineSchemaLibrary, response] = useUpsertPipelineSchemaLibrary({ successMsg, ...options });

  async function deployPipelineSchemas({ schemaLibraryVersion }) {
    const { pipelineId, pipelineName, writekey, clusterId, schemaLibraryVersionId, schemaLibraryName, eventSchemas } =
      extractPipelineSchemaLibraryData({ pipeline, schemaLibraryVersion });

    const controlApiSchemas = prepareSchemasForControlApi({ writekey, eventSchemas });

    const controlResponse = await deployToControlApi({
      path: PATH.PipelineSchemaLibrary,
      method: METHOD.POST,
      body: {
        library: {
          writeKey: writekey,
          author: user.email,
          schemas: controlApiSchemas,
        },
      },
      clusterId,
      writekey,
    });

    if (controlResponse?.errors) {
      return controlResponse;
    }

    const mutationResponse = await upsertPipelineSchemaLibrary({
      id: pipelineSchemaLibraryId,
      pipelineId,
      schemaLibraryVersionId,
    });

    if (!mutationResponse.errors) {
      trackWithUser({
        clusterId,
        pipelineWritekey: writekey,
        pipelineName,
        schemaLibraryName,
        schemaLibraryVersionId,
      });
    }

    return mutationResponse;
  }

  return [deployPipelineSchemas, response];
}

export default useDeployPipelineSchemas;
